import { useState } from "react";
import { Box, Typography, Avatar, Grid, Card, Switch, Button } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

import { colors, roleColors } from "../../ui/colors";
import { filledButton } from "../../ui/styles/buttonStyles";
import { renderIconResidential } from "../helpers/renderIconResidential";

export function ResidentialServices({ services = [], updateSelectedServices, showSnackbar }) {
  const [changedServices, setChangedServices] = useState({});

  const handleToggle = (serviceId, newValue) => {
    setChangedServices((prev) => ({
      ...prev,
      [serviceId]: newValue,
    }));
  };

  const hasChanges = Object.keys(changedServices).length > 0;

  const handleSave = async () => {
    try {
      await updateSelectedServices(changedServices);
      showSnackbar("Services updated successfully", "success");
    } catch (error) {
      showSnackbar("An error occurred while updating services", "error");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: { xs: 1.5, sm: 2, md: 3 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 2.5 },
        }}
      >
        {/* Header card */}
        <Box
          sx={{
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2.5, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: { xs: 58, sm: 68 },
                height: { xs: 58, sm: 68 },
                bgcolor: roleColors.residential.base,
              }}
            >
              <HomeIcon sx={{ fontSize: { xs: 29, sm: 34 }, color: "#fff" }} />
            </Avatar>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: colors.gray[400],
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Services
              </Typography>

              <Typography
                sx={{
                  color: colors.gray[900],
                  fontWeight: 800,
                  fontSize: { xs: "1.35rem", sm: "1.7rem" },
                  lineHeight: 1.1,
                }}
              >
                Residential Services
              </Typography>

              <Typography
                sx={{
                  color: colors.gray[500],
                  fontSize: { xs: "0.82rem", sm: "0.9rem" },
                  mt: 0.5,
                  fontWeight: 600,
                }}
              >
                Manage your residential services
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              bgcolor: hasChanges ? "rgba(226, 135, 67, 0.12)" : "var(--fx-surface-muted, #F1F5F9)",
              color: hasChanges ? roleColors.residential.base : colors.gray[500],
              fontWeight: 800,
              fontSize: "0.75rem",
            }}
          >
            {hasChanges ? "Unsaved changes" : "All saved"}
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: colors.gray[900],
                fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.2rem" },
              }}
            >
              Available services
            </Typography>

            <Typography
              sx={{
                color: colors.gray[500],
                fontSize: "0.85rem",
                fontWeight: 600,
                mt: 0.25,
              }}
            >
              Enable or disable the services your company offers.
            </Typography>
          </Box>

          {services.length > 0 ? (
            <Grid
              container
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {services.map((service) => {
                const defaultActive = service.companyServices?.[0]?.isActive ?? false;

                const active = changedServices[service.id] !== undefined ? changedServices[service.id] : defaultActive;

                return (
                  <Grid item key={service.id} sx={{ display: "contents" }}>
                    <Card
                      elevation={0}
                      sx={{
                        p: { xs: 1.75, sm: 2 },
                        borderRadius: 3,
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        boxShadow: "none",
                        border: "none",
                        minHeight: 132,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: 2.5,
                              bgcolor: active ? "rgba(226, 135, 67, 0.1)" : "var(--fx-surface-muted, #F1F5F9)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {renderIconResidential(service.workServices?.name, active)}
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: colors.gray[900],
                                fontWeight: 800,
                                fontSize: { xs: "0.95rem", sm: "1rem" },
                                lineHeight: 1.25,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={service.workServices?.name}
                            >
                              {service.workServices?.name || "Unnamed service"}
                            </Typography>

                            <Typography
                              sx={{
                                color: colors.gray[500],
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                mt: 0.35,
                              }}
                            >
                              Residential service
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: active ? "rgba(226, 135, 67, 0.12)" : "var(--fx-surface-muted, #F1F5F9)",
                            color: active ? roleColors.residential.base : colors.gray[500],
                            fontWeight: 800,
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {active ? "Active" : "Inactive"}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: colors.gray[500],
                            fontSize: "0.8rem",
                            fontWeight: 700,
                          }}
                        >
                          Service availability
                        </Typography>

                        <Switch
                          checked={active}
                          onChange={() => handleToggle(service.id, !active)}
                          size="medium"
                          sx={{
                            flexShrink: 0,
                            "& .MuiSwitch-track": {
                              bgcolor: active ? `${roleColors.residential[200]} !important` : `#d8dce2 !important`,
                              opacity: "1 !important",
                            },
                            "& .MuiSwitch-thumb": {
                              bgcolor: active ? roleColors.residential.base : "#fff",
                            },
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              sx={{
                minHeight: 280,
                borderRadius: 4,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <CleaningServicesIcon
                  sx={{
                    fontSize: 34,
                    color: colors.gray[400],
                  }}
                />
              </Box>

              <Typography
                sx={{
                  color: colors.gray[800],
                  fontWeight: 800,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                }}
              >
                No services available
              </Typography>

              <Typography
                sx={{
                  color: colors.gray[500],
                  maxWidth: 330,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  mt: 0.75,
                }}
              >
                Residential services will appear here once they are configured.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1.5, sm: 2 },
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            disabled={!hasChanges}
            onClick={handleSave}
            sx={{
              ...filledButton,
              minWidth: { xs: "100%", sm: 180 },
              height: 44,
              borderRadius: 999,
              fontSize: "0.9rem",
              fontWeight: 800,
              textTransform: "none",
              boxShadow: "none",
              bgcolor: hasChanges ? colors.orange.base : colors.gray[200],
              color: hasChanges ? "#fff" : colors.gray[500],
              "&:hover": {
                boxShadow: "none",
                bgcolor: hasChanges ? colors.orange[600] : colors.gray[200],
              },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
